import React, { useEffect, useMemo, useState } from "react";
import { getKapasitetsOverstyringer } from "../../lib/tjenestekatalogApi";
import {
  isoDate,
  weekStartISO,
  tidsstyrtVerdi,
  ALL_STORES,
  DAGSMÅL_PER_BUTIKK,
  isDateClosed,
  fmtKr,
} from "./workshopCapacityUtils";
import WorkshopDagDetaljerModal from "./WorkshopDagDetaljerModal";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// storeSlug: "alle" | "sport1royken" | "sport1slemmestad" | "roykensykkelverksted"
export default function WorkshopTodayRevenueBar({ storeSlug }) {
  const [loading, setLoading] = useState(true);
  const [details, setDetails] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);

  const isAlle = storeSlug === "alle";
  const slugs = useMemo(
    () => (isAlle ? ALL_STORES : [storeSlug]),
    [isAlle, storeSlug]
  );

  const todayISO = useMemo(() => isoDate(new Date()), []);
  const weekISO = useMemo(() => weekStartISO(new Date()), []);

  useEffect(() => {
    getKapasitetsOverstyringer()
      .then((result) => setOverrides(result || []))
      .catch(() => setOverrides([]));
  }, []);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all(
      slugs.map((slug) =>
        fetch(
          `${API_BASE_URL}/workshop/capacity-history?verkstedSlug=${encodeURIComponent(slug)}&from=${todayISO}&to=${todayISO}`
        ).then((r) => (r.ok ? r.json() : { details: [] }))
      )
    )
      .then((responses) => {
        if (!alive) return;
        setDetails(responses.flatMap((r) => (r.details || []).filter((d) => !d.reason)));
      })
      .catch(() => {
        if (alive) setDetails([]);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [slugs, todayISO]);

  const todayServices = useMemo(
    () => details.filter((d) => d.ferdigDato === todayISO),
    [details, todayISO]
  );

  const dagsmål = useMemo(
    () =>
      slugs.reduce((sum, slug) => {
        if (isDateClosed(overrides, slug, todayISO, weekISO)) return sum;
        return sum + (DAGSMÅL_PER_BUTIKK[slug] || 0);
      }, 0),
    [slugs, overrides, todayISO, weekISO]
  );

  const arbeidskost = todayServices.reduce((s, d) => s + (d.omsetning || 0), 0);
  const tidsstyrtMin = todayServices.reduce((s, d) => s + (d.tidsstyrtMin || 0), 0);
  const tidsstyrt = tidsstyrtVerdi(tidsstyrtMin);
  const omsetning = arbeidskost + tidsstyrt;
  const antall = todayServices.length;
  const snittpris = antall > 0 ? Math.round(omsetning / antall) : 0;

  const pct = dagsmål > 0 ? Math.min(100, Math.round((omsetning / dagsmål) * 100)) : null;
  const goalReached = dagsmål > 0 && omsetning >= dagsmål;

  if (loading) return null;

  const clickable = antall > 0;

  return (
    <>
      <WorkshopDagDetaljerModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={`Dagens omsetning ${todayISO} — ${fmtKr(omsetning)}`}
        services={todayServices}
        showButikk={isAlle}
      />

      <div
        className={`rounded-lg border bg-white px-4 py-2.5 flex items-center gap-3 flex-wrap ${clickable ? "cursor-pointer hover:bg-gray-50" : ""}`}
        onClick={clickable ? () => setModalOpen(true) : undefined}
        title={clickable ? "Klikk for servicedetaljer" : undefined}
      >
        <div className="text-sm text-gray-600 shrink-0">Dagens omsetning</div>

        <div className={`text-lg font-bold shrink-0 ${goalReached ? "text-green-600" : ""}`}>
          {fmtKr(omsetning)}
        </div>

        {antall > 0 && (
          <div className="text-xs text-gray-500 shrink-0">
            {antall} service{antall === 1 ? "" : "r"} avsluttet · snitt {fmtKr(snittpris)}/service
            {tidsstyrt > 0 && (
              <> · <span className="text-orange-600">{fmtKr(tidsstyrt)} tidsstyrt</span></>
            )}
          </div>
        )}

        {dagsmål > 0 ? (
          <div className="flex items-center gap-2 flex-1 min-w-[140px]">
            <div className="relative h-2 flex-1 rounded-full bg-gray-100 overflow-hidden">
              <div
                className={`h-full rounded-full ${
                  goalReached ? "bg-green-500" : pct >= 70 ? "bg-green-400" : pct >= 40 ? "bg-amber-400" : "bg-red-400"
                }`}
                style={{ width: `${pct}%` }}
              />
            </div>
            <span className={`text-xs font-medium shrink-0 ${goalReached ? "text-green-700" : "text-gray-500"}`}>
              {pct} % av {fmtKr(dagsmål)}
            </span>
          </div>
        ) : (
          <div className="text-xs text-gray-400">Ingen dagsmål satt</div>
        )}
      </div>
    </>
  );
}
